import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth, hasRole } from "../lib/auth";
import { ROLE_LABELS } from "../lib/apiClient";
import {
  LayoutDashboard, ClipboardList, ScanLine, Tag, Factory, Truck, Users, History,
  LogOut, PlusCircle, Package, Ruler, ShieldCheck, BarChart3, LayoutGrid, Menu, X, ListChecks, Key,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Üretim Panosu", icon: LayoutDashboard, roles: null, end: true, testid: "nav-dashboard" },
  { to: "/siparisler", label: "Siparişler", icon: ClipboardList, roles: null, testid: "nav-orders" },
  { to: "/panel", label: "Sipariş Paneli", icon: LayoutGrid, roles: null, testid: "nav-panel" },
  { to: "/uretim", label: "Üretim Takibi", icon: Factory, roles: null, testid: "nav-production" },
  { to: "/kalite", label: "Kalite Kontrol", icon: ShieldCheck, roles: ["production_manager", "production_operator"], testid: "nav-qc" },
  { to: "/tara", label: "Barkod Tarama", icon: ScanLine, roles: ["production_manager", "production_operator", "shipping_operator"], testid: "nav-scan" },
  { to: "/etiketler", label: "Etiket Yönetimi", icon: Tag, roles: ["production_manager"], testid: "nav-labels" },
  { to: "/sevkiyatlar", label: "Sevkiyatlar", icon: Truck, roles: null, testid: "nav-shipments" },
  { to: "/sevkiyat-paneli", label: "Sevkiyat Paneli", icon: LayoutGrid, roles: null, testid: "nav-shipment-panel" },
  { to: "/raporlar", label: "Raporlar", icon: BarChart3, roles: ["production_manager"], testid: "nav-reports" },
  { to: "/gecmis", label: "Geçmiş", icon: History, roles: null, testid: "nav-history" },
  { to: "/kumaslar", label: "Kumaşlar", icon: Package, roles: ["production_manager"], testid: "nav-fabrics" },
  { to: "/olculer", label: "Ölçüler", icon: Ruler, roles: ["production_manager"], testid: "nav-sizes" },
  { to: "/kurallar", label: "Parça Kuralları", icon: ListChecks, roles: ["production_manager"], testid: "nav-piece-rules" },
  { to: "/kullanicilar", label: "Kullanıcılar", icon: Users, roles: [], testid: "nav-users" },
  { to: "/api-anahtarlari", label: "API Anahtarları", icon: Key, roles: [], testid: "nav-api-keys" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close mobile drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Lock body scroll when drawer open
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  const currentTitle = NAV.find(n => n.end ? location.pathname === n.to : location.pathname.startsWith(n.to))?.label || "MARSELLA";

  const SidebarContent = (
    <>
      <div className="h-16 px-5 flex items-center justify-between border-b border-slate-800 shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-blue-600 grid place-items-center font-display font-black text-white text-lg">M</div>
          <div>
            <div className="font-display font-black text-lg leading-none">MARSELLA</div>
            <div className="text-[10px] tracking-[0.2em] text-slate-400 uppercase">Üretim Takip</div>
          </div>
        </div>
        <button
          onClick={() => setDrawerOpen(false)}
          data-testid="sidebar-close-btn"
          className="lg:hidden text-slate-300 hover:text-white p-1"
          aria-label="Menüyü kapat"
        >
          <X size={22} />
        </button>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV.map((item) => {
          if (item.roles !== null && !hasRole(user, ...item.roles)) return null;
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) =>
                [
                  "flex items-center gap-3 px-3 py-2.5 text-sm font-semibold border-l-2",
                  isActive
                    ? "bg-slate-800 text-white border-blue-500"
                    : "text-slate-300 hover:bg-slate-800 hover:text-white border-transparent",
                ].join(" ")
              }
            >
              <Icon size={18} strokeWidth={2.25} />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>

      <div className="border-t border-slate-800 p-4 shrink-0">
        <div className="mb-3">
          <div className="text-sm font-semibold truncate" data-testid="current-user-name">{user?.name}</div>
          <div className="text-xs text-slate-400 truncate" data-testid="current-user-role">{ROLE_LABELS[user?.role] || user?.role}</div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={async () => { await logout(); navigate("/giris"); }}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-100 text-sm font-semibold rounded-none border border-slate-700"
        >
          <LogOut size={16} /> Çıkış Yap
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50 lg:flex">
      {/* Desktop persistent sidebar */}
      <aside className="hidden lg:flex w-64 bg-slate-900 text-slate-100 flex-col border-r border-slate-800 shrink-0">
        {SidebarContent}
      </aside>

      {/* Mobile drawer + backdrop */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 lg:hidden"
          onClick={() => setDrawerOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] bg-slate-900 text-slate-100 flex flex-col border-r border-slate-800 transform transition-transform duration-300 lg:hidden ${drawerOpen ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="mobile-sidebar"
      >
        {SidebarContent}
      </aside>

      <main className="flex-1 min-w-0">
        {/* Mobile top bar */}
        <header className="lg:hidden sticky top-0 z-30 bg-slate-900 text-white h-14 flex items-center px-3 gap-3 border-b border-slate-800 shadow-md">
          <button
            onClick={() => setDrawerOpen(true)}
            data-testid="mobile-menu-btn"
            className="p-2 -ml-1 hover:bg-slate-800 rounded"
            aria-label="Menüyü aç"
          >
            <Menu size={24} />
          </button>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="w-7 h-7 bg-blue-600 grid place-items-center font-display font-black text-white text-sm shrink-0">M</div>
            <div className="font-display font-black text-base truncate">{currentTitle}</div>
          </div>
          <div className="text-[10px] text-slate-400 uppercase tracking-widest hidden sm:block truncate max-w-[100px]">
            {user?.name}
          </div>
        </header>

        <div className="max-w-[1600px] mx-auto p-4 sm:p-6 lg:p-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <header className="mb-6 lg:mb-8 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-6">
      <div className="min-w-0">
        <h1 className="font-display font-black text-2xl sm:text-3xl md:text-4xl text-slate-900 tracking-tight">{title}</h1>
        {subtitle && <p className="text-slate-500 mt-1 font-medium text-sm sm:text-base">{subtitle}</p>}
      </div>
      {actions && <div className="flex gap-2 items-center flex-wrap">{actions}</div>}
    </header>
  );
}

export function ActionButton({ children, ...props }) {
  return (
    <button
      {...props}
      className={
        "inline-flex items-center gap-2 px-5 py-3 bg-blue-700 hover:bg-blue-800 text-white font-bold text-sm uppercase tracking-wide rounded-none border border-blue-800 active:translate-y-[1px] transition-transform " +
        (props.className || "")
      }
    >
      {children}
    </button>
  );
}

export { PlusCircle };
